import React, { useState, useMemo, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import {
  Button,
  Icon,
  CheckboxFilter,
  CheckboxFilterOption,
} from "../../components/tailwind";
import {
  useGetCustomerFieldFilterOptionsQuery,
  FieldTypeChoice,
} from "../../api/customerFieldApi";

// Special value for fields without entity/type
const NONE_VALUE = "__none__";

export type CustomerFieldFiltersState = {
  search: string;
  entityIds: string[];
  types: string[];
};

type Props = {
  filters: CustomerFieldFiltersState;
  onFiltersChange: (filters: CustomerFieldFiltersState) => void;
  fieldTypeChoices: FieldTypeChoice[];
  isFiltering?: boolean;
};

export const CustomerFieldFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  fieldTypeChoices,
  isFiltering = false,
}) => {
  const {
    data: filterOptions,
    isLoading: loadingOptions,
  } = useGetCustomerFieldFilterOptionsQuery();

  const [localSearch, setLocalSearch] = useState(filters.search);

  // Sync local search state when filters.search prop changes (e.g., when cleared)
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Debounced search handler
  const debouncedSearchChange = useCallback(
    debounce((value: string) => {
      onFiltersChange({ ...filters, search: value });
    }, 300),
    [filters, onFiltersChange]
  );

  const handleSearchChange = (value: string): void => {
    setLocalSearch(value);
    debouncedSearchChange(value);
  };

  const handleEntityIdsChange = (entityIds: string[]): void => {
    onFiltersChange({ ...filters, entityIds });
  };

  const handleTypesChange = (types: string[]): void => {
    onFiltersChange({ ...filters, types });
  };

  const handleClearFilters = (): void => {
    setLocalSearch("");
    onFiltersChange({ search: "", entityIds: [], types: [] });
  };

  // Build entity_id options
  const entityIdOptions: CheckboxFilterOption[] = useMemo(() => {
    const options: CheckboxFilterOption[] = [];
    if (filterOptions?.has_empty_entity) {
      options.push({ value: NONE_VALUE, label: "(No entity)" });
    }
    if (filterOptions?.entity_ids) {
      options.push(
        ...filterOptions.entity_ids.map((id) => ({ value: id, label: id }))
      );
    }
    return options;
  }, [filterOptions]);

  // Build type options using fieldTypeChoices for labels
  const typeOptions: CheckboxFilterOption[] = useMemo(() => {
    const options: CheckboxFilterOption[] = [];
    if (filterOptions?.has_empty_type) {
      options.push({ value: NONE_VALUE, label: "(No type)" });
    }
    if (filterOptions?.types) {
      options.push(
        ...filterOptions.types.map((type) => {
          const choice = fieldTypeChoices.find((c) => c.value === type);
          return { value: type, label: choice?.label || type };
        })
      );
    }
    return options;
  }, [filterOptions, fieldTypeChoices]);

  const hasActiveFilters =
    filters.search.trim() !== "" ||
    filters.entityIds.length > 0 ||
    filters.types.length > 0;

  return (
    <div className="tw-mb-6 tw-space-y-4">
      {/* Search Bar - Own Row */}
      <div>
        <div className="tw-flex tw-gap-2 tw-max-w-md">
          <div className="tw-flex-1 tw-relative">
            <input
              type="text"
              value={localSearch}
              onChange={(e): void => handleSearchChange(e.target.value)}
              placeholder="Search by field ID..."
              className="tw-w-full tw-pl-3 tw-pr-8 tw-py-3 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-400 focus:tw-border-transparent "
              data-testid="customer-field-search"
            />
            {localSearch && (
              <button
                type="button"
                onClick={(): void => handleSearchChange("")}
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
              >
                <Icon name="close" className="tw-text-lg" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters - Grid Layout */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 tw-items-end">
        {/* Entity ID Filter */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Entity
          </label>
          <CheckboxFilter
            options={entityIdOptions}
            selected={filters.entityIds}
            onChange={handleEntityIdsChange}
            placeholder="Select entities..."
            disabled={loadingOptions}
            testIdBase="entity-filter"
          />
        </div>

        {/* Type Filter */}
        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
            Type
          </label>
          <CheckboxFilter
            options={typeOptions}
            selected={filters.types}
            onChange={handleTypesChange}
            placeholder="Select types..."
            disabled={loadingOptions}
            testIdBase="type-filter"
          />
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div>
            <Button
              variant="secondary-alt"
              size="sm"
              icon
              onClick={handleClearFilters}
              data-testid="clear-filters-btn"
            >
              <Icon name="delete" />
              Clear filters
            </Button>
          </div>
        )}

        {/* Loading indicator */}
        {isFiltering && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500">
            <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-gray-500"></div>
            Loading...
          </div>
        )}
      </div>
    </div>
  );
};
